import type { PropsWithChildren } from "react";
import * as Yup from "yup";

import { getEnvironmentFlags } from "@krea/shared/config/catalog";
import { ConsentCheckboxValidation } from "@krea/shared/design-system/fields/Checkbox/validation";
import { EmailValidation } from "@krea/shared/design-system/fields/EmailInput/validation";
import { OrganisationValidation } from "@krea/shared/design-system/fields/OrganisationInput/validation";
import { MobilePhoneNumberValidation } from "@krea/shared/design-system/fields/PhoneInput/validation";
import { RequestedLoanAmountValidation } from "@krea/shared/design-system/fields/RequestedLoanAmount/validation";
import {
  CountryCode,
  type IPreliminaryLoanAmountData,
  type SupportedLanguage,
} from "@krea/shared/types/common";
import { privacyPolicyLink } from "@krea/shared/utils/privacyPolicyLink";

export interface ICustomExceptionBody {
  errors?: { code: string }[];
}

export function getAdtractionRedirectUrl(params: {
  countryCode: CountryCode;
  environment: string;
  ourLink: string;
  affiliatePartnerId: string;
}): string | null {
  const { countryCode, environment, ourLink, affiliatePartnerId } = params;
  if (!getEnvironmentFlags(environment).affiliateRedirects) {
    return null;
  }
  switch (countryCode) {
    case CountryCode.SE: {
      const SEAdtractionId = "1301012751";
      return `https://go.adt253.net/t/t?a=${SEAdtractionId}&as=${encodeURIComponent(affiliatePartnerId)}&t=2&tk=1&url=${encodeURIComponent(ourLink)}`;
    }
    case CountryCode.FI: {
      const FIAdtractionId = "1748120981";
      return `https://go.adt253.net/t/t?a=${FIAdtractionId}&as=${encodeURIComponent(affiliatePartnerId)}&t=2&tk=1&url=${encodeURIComponent(ourLink)}`;
    }
    default: {
      const exhaustiveCheck: never = countryCode;
      console.error(`Exhaustive check failed: ${exhaustiveCheck}`);
      return null;
    }
  }
}

export function readAffiliateCookieUtm(): Record<string, string> | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    /(?:^|;\s*)affiliate-query-params=([^;]*)/,
  );
  if (!match?.[1]) return null;
  try {
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

export const getUtmData = (
  utmSourceIdentifier: string | null = null,
  utmCampaign: string | null = null,
): Record<string, string> => {
  if (utmSourceIdentifier) {
    const result: Record<string, string> = {
      // biome-ignore lint/style/useNamingConvention: UTM params are standardized snake_case
      utm_medium: "widget",
      // biome-ignore lint/style/useNamingConvention: UTM params are standardized snake_case
      utm_source: utmSourceIdentifier,
    };
    if (utmCampaign) result.utm_campaign = utmCampaign;
    return result;
  }

  return { ...(readAffiliateCookieUtm() ?? {}) };
};

export const getValidationSchema = (countryCode: CountryCode) =>
  Yup.object().shape({
    organisation: OrganisationValidation(countryCode),
    email: EmailValidation(),
    mobilePhoneNumber: MobilePhoneNumberValidation(countryCode),
    consentCheckbox: ConsentCheckboxValidation(),
    requestedLoanAmount: RequestedLoanAmountValidation(countryCode),
  });

export type ValidationSchemaType = Yup.InferType<
  ReturnType<typeof getValidationSchema>
>;

export function getDefaultRequestedLoanAmount(
  countryCode: CountryCode,
): number {
  switch (countryCode) {
    case CountryCode.SE:
      return 600_000;
    case CountryCode.FI:
      return 60_000;
    default: {
      const exhaustive: never = countryCode;
      console.error(`Unexpected country: ${exhaustive}`);
      return 600_000;
    }
  }
}

export function PrivacyPolicyAnchor({
  countryCode,
  countryWebsiteUrl,
  language,
  children,
}: PropsWithChildren<{
  countryCode: CountryCode;
  countryWebsiteUrl: string;
  language: SupportedLanguage;
}>) {
  return (
    <a
      target="_blank"
      href={privacyPolicyLink(countryCode, countryWebsiteUrl, language)}
      rel="noopener noreferrer"
      className="font-semibold text-primary no-underline hover:underline"
    >
      {children}
    </a>
  );
}

export interface IPersistedWidgetState {
  organisation?: { name?: string; organisationNumber?: string };
  email?: string;
  mobilePhoneNumber?: string;
  requestedLoanAmount?: number;
  consentCheckbox?: boolean;
  preliminaryBid?: IPreliminaryLoanAmountData;
  showResult?: boolean;
}

export const sessionStorageSafe = {
  get: (key: string): string | null => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string): void => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.sessionStorage.setItem(key, value);
    } catch {
      /* no-op */
    }
  },
};

export function loadPersistedWidgetState(
  key: string,
): IPersistedWidgetState | null {
  const raw = sessionStorageSafe.get(key);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as IPersistedWidgetState;
  } catch {
    return null;
  }
}

// Next.js returns StaticImageData objects; Vite returns plain string URLs.
export const resolveImageSrc = (img: unknown): string =>
  img !== null && typeof img === "object" && "src" in img
    ? (img as { src: string }).src
    : (img as string);

export const compensationTiers: [threshold: number, compensation: number][] = [
  [100_000, 1_000],
  [200_000, 1_500],
  [300_000, 2_500],
  [400_000, 3_000],
  [500_000, 4_000],
  [600_000, 5_000],
  [700_000, 6_000],
  [800_000, 7_000],
  [900_000, 8_000],
  [1_000_000, 9_000],
  [1_300_000, 10_000],
  [1_700_000, 11_000],
  [1_800_000, 12_000],
  [1_900_000, 13_000],
  [2_000_000, 14_000],
  [3_000_000, 15_000],
  [4_000_000, 20_000],
  [10_000_000, 25_000],
  [20_000_000, 30_000],
  [50_000_000, 40_000],
];

export const foretagarnaMemberCompensation = (amount: number) =>
  compensationTiers.find(([threshold]) => amount < threshold)?.[1] ?? 50_000;

/** Marks the element a loan widget recentres on step changes (see `recenterInViewport`). */
export const LOAN_WIDGET_SCROLL_TARGET_ATTR = "data-loan-widget-scroll-target";

/**
 * Brings `el` back into view after the widget changes step (form → loading →
 * result): each step has a different height, so without this the page keeps its
 * scroll position and the new step lands half under the sticky header or below
 * the fold (KREA-4760).
 *
 * Centres `el` in the space below the header when it fits, otherwise aligns its
 * top just under the header. The header offset is the element's own
 * `scroll-margin-top`, so each host sets it to match its header.
 */
export const recenterInViewport = (el: HTMLElement): void => {
  const rect = el.getBoundingClientRect();
  const headerOffset =
    Number.parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  const available = window.innerHeight - headerOffset;
  const centredGap =
    rect.height < available ? (available - rect.height) / 2 : 0;
  const top = window.scrollY + rect.top - headerOffset - centredGap;
  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  window.scrollTo({
    top: Math.max(0, top),
    behavior: reduceMotion ? "auto" : "smooth",
  });
};
